import * as dom from '../../util/easy-dom';
import {cmptMenu} from '../../util/cmpt-common-features';
import * as store from '../../util/store';
import * as events from '../../util/event-bus';
import * as env from '../../util/env';
import { buildDomainSwitchHref } from '../../util/url-helpers';
import { TASK_SERVICE_ERRORS, TASK_ATTENTION_TEXT, TASK_DEFAULT_DOMAIN } from '../../util/constants';

/**
 * Displays tasks
 * @class Sidekick.Tasks
 * @memberOf Sidekick
 * @param {EasyDom} elem - Container {@link EasyDom} element
 * @param {Object} attrs
 * @param {Object} ctx
 * @param {Function} i18n - translation function
 * @emits task.delete
 * @emits task.mark-as-done
 * @listens store tasks
 * @example Array of tasks
 * title: Task Title
 * comment: Optional. Task detail text
 * priority: Task priority
 * assignee: Task assignee
 * assignTo: Task target user
 * dueDate: Task due date
 * redirectUrl: Task redirect URL
 * completed: Shows the task as completed
 * {
	title: 'this is task 1',
  comment: 'You have a new task...',
  priority: 'High',
  dueDate: '2026-07-20',
  redirectUrl: '/tasks/1',
  completed: false
}, {
	title: 'this is task 2',
  comment: 'You have a new task... again!',
  priority: 'Medium',
  dueDate: '2026-07-22',
  redirectUrl: '/tasks/2',
  completed: true
}
 */
export default function (elem, attrs, ctx, i18n) {
  const { responsiveTopNav } = store.get('sk-config');
  const READ_KEY_PREFIX = 'sk-tasks-read-';
  const MAX_READ_IDS = 500;
  let currentItems = [];
  let panelOpened = false;
  let readIds = new Set();
  let readIdsKey = null;
  setViewMoreTasksLink();
  window.addEventListener('popstate', setViewMoreTasksLink);

  // Find DOM elements
  const domElements = {
    desktop: {
      cont: elem.find('.menulist')[0],
      menu: elem.find('.menu')[0],
      task: elem.find('.control')[0],
      unreadBadge: elem.find('.control .sk--unread')[0],
      menuCloseIcon: elem.find('.ss-ctrl-clear')[0],
      viewMoreLink: elem.find('.menu .view-more-tasks-btn')[0],
      subtitle: elem.find('.sk--tasks-subtitle')[0]
    },
    mobile: {
      cont: elem.find('.menulist-mobile')[0],
      menu: elem.find('.menu-tasks-mobile')[0],
      task: elem.find('.control-responsive-tasks')[0],
      unreadBadge: elem.find('.control-responsive-tasks .sk--unread')[0],
      taskBtn: elem.find('.control-responsive-tasks')[0],
      viewMoreLink: elem.find('.menu-tasks-mobile .view-more-tasks-btn')[0]
    },
  };

  const domButtonElements = document.querySelectorAll('.tasks-btn');
  const domMenuElements = document.querySelectorAll('.menu-tasks');

  if (responsiveTopNav) {
    domButtonElements.forEach((domButtonElement) => {
      domButtonElement.classList.add('sk-task-responsive');
    });
    domMenuElements.forEach((domMenuElement) => {
      domMenuElement.classList.add('sk-task-menu-responsive');
    });
    if (attrs.role === 'navitem') {
      domElements.mobile.taskBtn.css('display', 'block');
      if (ctx.url != null) {
        domElements.mobile.task.attr('target', ctx.target);
        events.on('tasks.unread-count', setUnreadBadge, true);
        return;
      }
      domElements.mobile.taskBtn.on('click', (evt) => {
        evt.stopPropagation();
        evt.preventDefault();
        const displayMobileMenu = domElements.mobile.menu.css('display');
        const displayAction = displayMobileMenu === 'none'? 'block' : 'none';
        domElements.mobile.menu.css('display', displayAction);
        // the mobile panel does not emit sk-menu-open/close, so track its open state here
        if (displayAction === 'block') { onPanelOpen(); } else { onPanelClose(); }
      });
    }
  }

  // Handle initial state and data changes
  // link-only mode has no panel to open, so the host-reported count is shown as is
  if (ctx.url != null) {
    domElements.desktop.task.attr('target', ctx.target);
    events.on('tasks.unread-count', setUnreadBadge, true);
    return;
  }

  store.onValue('tasks', updateAllTasks);
  // the count comes from the unread state kept here, so a host/interface update just refreshes the badge
  events.on('tasks.unread-count', () => setUnreadBadge(countUnread(currentItems)), true);
  events.on('sk-menu-open', (e) => { if (e === elem) { onPanelOpen(); } });
  events.on('sk-menu-close', (e) => { if (e === elem) { onPanelClose(); } });

  // Listen for events and take action
  cmptMenu(elem, domElements.desktop.task, domElements.desktop.menu);
  domElements.desktop.menuCloseIcon.on('click', () => events.emit('sk-menu-close', elem));
  handleMenuEvent(domElements.desktop);
  if(responsiveTopNav) {
    handleMenuEvent(domElements.mobile);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function setViewMoreTasksLink() {
    const taskPageUrl = getTasksPageUrl();
    const onTasksPage = isCurrentPage(taskPageUrl);
    elem.find('.view-more-tasks-btn').forEach((linkEl) => {
      const rawLink = linkEl._;
      if (onTasksPage) {
        // already on the Tasks page: the link is disabled and explains why
        rawLink.removeAttribute('href');
        rawLink.setAttribute('aria-disabled', 'true');
        rawLink.setAttribute('role', 'link');
        rawLink.setAttribute('tabindex', '0');
        rawLink.setAttribute('title', i18n('You are already on the Tasks page.'));
        linkEl.addClass('sk--disabled');
      } else {
        linkEl.attr('href', taskPageUrl);
        rawLink.removeAttribute('aria-disabled');
        rawLink.removeAttribute('role');
        rawLink.removeAttribute('tabindex');
        rawLink.removeAttribute('title');
        linkEl.removeClass('sk--disabled');
      }
      if (!rawLink._skDisabledClick) {
        rawLink._skDisabledClick = true;
        linkEl.on('click', (evt) => {
          if (linkEl.hasClass('sk--disabled')) { evt.preventDefault(); }
        });
      }
    });
  }

  function onPanelOpen() {
    panelOpened = true;
    setViewMoreTasksLink();
  }

  // closing the panel marks the tasks that were shown as read, which clears the badge
  function onPanelClose() {
    if (!panelOpened) { return; }
    panelOpened = false;
    markShownTasksAsRead();
  }

  function isCurrentPage(url) {
    try {
      const target = new URL(url, window.location.href);
      const here = window.location;
      const normalize = (path) => path.replace(/\/+$/, '') || '/';
      // the query (the !domain switch) is ignored: the same page in any domain is still the Tasks page
      return target.origin === here.origin && normalize(target.pathname) === normalize(here.pathname);
    } catch (e) {
      return false;
    }
  }

  // ── unread state ───────────────────────────────────────────────────────────────
  // Read ids are kept per user in localStorage so the badge survives a page reload.
  function getCurrentUser() {
    const passportData = store.get('passport-data');
    return passportData && passportData.legacyFormattedData && passportData.legacyFormattedData.user;
  }

  // null while the user is not known yet: read state then lives in memory only, never under a shared key
  function getReadKey() {
    const user = getCurrentUser();
    return user && user.profileId != null ? READ_KEY_PREFIX + user.profileId : null;
  }

  // the read ids follow the current user, so they are (re)loaded whenever the user changes
  function getReadIds() {
    const key = getReadKey();
    if (key !== readIdsKey) {
      readIdsKey = key;
      readIds = new Set();
      if (key) {
        try {
          const saved = JSON.parse(window.localStorage.getItem(key));
          readIds = new Set(Array.isArray(saved) ? saved : []);
        } catch (e) { /* unreadable: start with nothing read */ }
      }
    }
    return readIds;
  }

  function saveReadIds() {
    const key = getReadKey();
    if (!key) { return; }
    try {
      const ids = Array.from(getReadIds()).slice(-MAX_READ_IDS);
      window.localStorage.setItem(key, JSON.stringify(ids));
    } catch (e) { /* storage unavailable: read state lasts for this page only */ }
  }

  // tasks without an id are keyed by title and creation time, never by editable fields like the due date
  function getTaskKey(item) {
    return item.id != null ? String(item.id) : `${item.title}|${item.createdAt}`;
  }

  // A task the current user created for themselves must not raise the badge.
  function isSelfAssigned(item) {
    if (!item.createdBy) { return false; }
    const user = getCurrentUser();
    if (user && user.profileId != null) { return String(item.createdBy) === String(user.profileId); }
    const assignedTo = item.assignee || item.assignTo;
    return !!assignedTo && String(item.createdBy) === String(assignedTo);
  }

  function isUnread(item) {
    return !item.completed
      && item.unread !== false
      && !isSelfAssigned(item)
      && !getReadIds().has(getTaskKey(item));
  }

  function countUnread(items) {
    return items.filter(isUnread).length;
  }

  function markShownTasksAsRead() {
    const newlyRead = currentItems.filter(isUnread);
    if (newlyRead.length === 0) { return; }
    newlyRead.forEach((item) => getReadIds().add(getTaskKey(item)));
    saveReadIds();
    setUnreadBadge(countUnread(currentItems));
    events.emit('task.mark-as-read', newlyRead);
  }

  function getTasksPageUrl() {
    let envName = 'prod';
    const passportData = store.get('passport-data');
    const passportCfg = store.get('passport');

    if (passportCfg !== null && passportData !== null) {
      envName = env.environmentFromPassportDomain(window.location.hostname);
    }

    const envValues = env[envName] || env.prod;
    const tasksPageUrl = envValues.tasksPageUrl || '/tasks';
    // the Tasks app lives in one configured domain, not necessarily the one the user is working in
    const taskConfig = (store.get('sk-config') || {}).taskConfig || {};
    const taskDomain = taskConfig.taskDomain === undefined ? TASK_DEFAULT_DOMAIN : taskConfig.taskDomain;
    return taskDomain ? buildDomainSwitchHref(tasksPageUrl, taskDomain) : tasksPageUrl;
  }

  // Update function for when task list changes
  function updateTaskList(newCtx, elements) {

    // Handle error scenarios
    if (newCtx && newCtx.error) {
      const domTasksError = dom.parse(`<li class="menulist-item" ><div style="color: red;">${i18n(TASK_SERVICE_ERRORS.DEFAULT)}</div></li>`);
      elements.cont.children(domTasksError);
      // Immediately clear the badge when error is detected
      setUnreadBadge(0);
      // the subtitle belongs to the previous payload, so clear it as well
      setSubtitle(0);
      return;
    }

		// update our context object
    if (!ctx || ctx.items !== newCtx.items) { ctx = newCtx; }
    const domTasksEmpty = dom.parse(`<li class="menulist-item tasks-empty"><div>${i18n('You have no tasks')}</div></li>`);
    const incompleteItems = ctx.items.filter(item => !item.completed);

    // No actionable items to display
    if (incompleteItems.length === 0) { elements.cont.children(domTasksEmpty); return; }

    // Process the list of items to display (filter out completed tasks)
    elements.cont.children(incompleteItems.map(item => {
      const taskMeta = getTaskMetaMarkup(item);
      const el = dom.parse(`
        <li class="menulist-item ${item.completed ? 'task-completed' : 'task-incomplete'} ${item.redirectUrl ? 'task-has-link' : ''}">
          <h4 class="subject">
            ${escapeHtml(item.title)}
          </h4>
          ${getTaskSummaryMarkup(item)}
          ${item.comment ? `<div class="task-comment">${escapeHtml(item.comment)}</div>` : ''}
          ${taskMeta}
          <button type="button" class="remove fa-li-close" aria-label="remove task"></button>
        </li>
      `);

			el.find('.remove')[0].on('click', evt => {
				evt.preventDefault();
				evt.stopPropagation();
        events.emit('task.delete', item);
			});

			el.on('click', function (evt) {
				evt.stopPropagation();
				if (!item.completed) {
					events.emit('task.mark-as-done', item);
				}
				if (item.redirectUrl) {
					window.open(item.redirectUrl, '_blank', 'noopener,noreferrer');
				}
				events.emit('task.action', item);
			});

			return el;
		}));

    if (!elements.menu.hasClass('ss-menu-open')) {
      elements.cont.find('=a').forEach(a => a.attr('tabindex', '-1'));
      elements.cont.find('=button').forEach(b => b.attr('tabindex', '-1'));
    }
  }

  function updateAllTasks(newCtx) {
    const normalizedCtx = newCtx && newCtx.error ? newCtx : normalizeTasksContext(newCtx);
    updateIncomplete(normalizedCtx);
    updateTaskList(normalizedCtx, domElements.desktop);
    if(responsiveTopNav) {
      updateTaskList(normalizedCtx, domElements.mobile);
    }
  }

  function updateIncomplete(taskContext) {
    // Don't update count if there's an error
    if (taskContext && taskContext.error) {
      currentItems = [];
      setUnreadBadge(0);
      return;
    }

    const items = taskContext && taskContext.items ? taskContext.items : [];
    currentItems = items;
    // the badge counts new (unread) tasks; the subtitle counts everything still open
    setUnreadBadge(countUnread(items));
    setSubtitle(items.filter(task => task.completed !== true).length);
  }

  // subtitle is only displayed by the Homeroom theme
  function setSubtitle(incomplete) {
    const text = (incomplete > 0
      ? `${incomplete} ${i18n(incomplete === 1 ? TASK_ATTENTION_TEXT.SINGULAR : TASK_ATTENTION_TEXT.PLURAL)}`
      : '');
    // the desktop panel and the responsive (mobile) panel each have one
    elem.find('.sk--tasks-subtitle').forEach((subtitle) => { subtitle._.textContent = text; });
  }

  function normalizeTasksContext(taskContext) {
    if (Object.prototype.toString.call(taskContext) === '[object Array]') {
      taskContext = { items: taskContext };
    }

    if (!taskContext || Object.prototype.toString.call(taskContext.items) !== '[object Array]') {
      return { items: [] };
    }

    const normalizedItems = taskContext.items.map((item, index) => normalizeTaskItem(item, index));
    const sortedItems = sortTasksNewestFirst(normalizedItems);
    return Object.assign({}, taskContext, { items: sortedItems });
  }

  function normalizeTaskItem(item = {}, sourceIndex) {
    const completed = typeof item.completed === 'boolean'
      ? item.completed
      : (typeof item.done === 'boolean' ? item.done : false);

    return Object.assign({}, item, {
      _sourceIndex: sourceIndex,
      title: item.title || i18n('Untitled task'),
      comment: item.comment != null ? item.comment : (item.content || ''),
      priority: item.priority || '',
      assignee: item.assignee || '',
      assignTo: item.assignTo || '',
      dueDate: item.dueDate || item.timestamp || '',
      createdAt: item.createdAt || item.createdDate || '',
      createdBy: item.createdBy || '',
      redirectUrl: item.redirectUrl || item.url || '',
      completed
    });
  }

  // newest created first; tasks without a creation date follow in their source order
  function sortTasksNewestFirst(items) {
    return items.slice().sort((itemA, itemB) => {
      const createdA = getTimestamp(itemA.createdAt);
      const createdB = getTimestamp(itemB.createdAt);

      if (createdA !== null && createdB !== null && createdA !== createdB) {
        return createdB - createdA;
      }
      if (createdA !== null && createdB === null) {
        return -1;
      }
      if (createdA === null && createdB !== null) {
        return 1;
      }

      return itemA._sourceIndex - itemB._sourceIndex;
    });
  }

  function getTimestamp(value) {
    if (!value) {
      return null;
    }

    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  // "Due today · Recruiting & Hiring": the single secondary line of the Homeroom theme (hidden by default)
  function getTaskSummaryMarkup(item) {
    const parts = [getTaskStatusText(item), item.comment].filter(Boolean);
    return parts.length ? `<div class="task-summary">${escapeHtml(parts.join(' · '))}</div>` : '';
  }

  function getTaskStatusText(item) {
    const priority = String(item.priority || '');
    if (priority.toLowerCase() === 'critical') {
      return `${i18n('Critical')} ${i18n('priority')}`;
    }
    const dueDate = getDueDateLabel(item.dueDate);
    return dueDate || (priority ? `${priority} ${i18n('priority')}` : '');
  }

  function getDueDateLabel(dueDate) {
    if (!dueDate) { return ''; }
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dueDate);
    const due = dateOnly ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]) : new Date(dueDate);
    if (Number.isNaN(due.getTime())) { return `${i18n('Due')} ${dueDate}`; }

    const now = new Date();
    const dayDiff = Math.round((new Date(due.getFullYear(), due.getMonth(), due.getDate()) -
      new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
    if (dayDiff === 0) { return i18n('Due today'); }
    if (dayDiff === 1) { return i18n('Due tomorrow'); }
    return `${i18n('Due')} ${dueDate}`;
  }

  function getTaskMetaMarkup(item) {
    const metaRows = [];

    if (item.priority) {
      metaRows.push(`<small class="task-meta"><span class="task-meta-label">${i18n('Priority')}:</span> ${escapeHtml(item.priority)}</small>`);
    }

    if (item.assignee) {
      metaRows.push(`<small class="task-meta"><span class="task-meta-label">${i18n('Assignee')}:</span> ${escapeHtml(item.assignee)}</small>`);
    }

    if (item.assignTo) {
      metaRows.push(`<small class="task-meta"><span class="task-meta-label">${i18n('Assign To')}:</span> ${escapeHtml(item.assignTo)}</small>`);
    }

    if (item.dueDate) {
      metaRows.push(`<small class="task-meta"><span class="task-meta-label">${i18n('Due Date')}:</span> ${escapeHtml(item.dueDate)}</small>`);
    }

    return metaRows.join('');
  }

  function setUnreadBadge(unread) {
    elem.toggleClass('has-unread', unread > 0);
    updateUnreadBadgeElement(domElements.desktop.unreadBadge, unread);

    if(responsiveTopNav) {
      updateUnreadBadgeElement(domElements.mobile.unreadBadge, unread);
    }
  }

  function updateUnreadBadgeElement(unreadBadgeElem, unreadCount) {
    unreadBadgeElem.css('display', 'none');
    unreadBadgeElem.removeClass('sk--max');

    if (unreadCount <= 0) {
      return;
    }

    const displayCount = unreadCount > 99 ? 99 : unreadCount;
    if (unreadCount > 99) {
      unreadBadgeElem.addClass('sk--max');
    }

    unreadBadgeElem._.innerHTML = displayCount;
    unreadBadgeElem.css('display', 'block');
  }

  function handleMenuEvent(elements) {
    events.on('sk-menu-close', function (e) {
      if (e !== elem) {
        return;
      }
      elements.cont.find('=a').forEach((a) => a.attr('tabindex', '-1'));
      elements.cont.find('=button').forEach((b) => b.attr('tabindex', '-1'));
      if (elements.viewMoreLink) {
        elements.viewMoreLink.attr('tabindex', '-1');
      }
    });
    events.on('sk-menu-open', function (e) {
      if (e !== elem) {
        return;
      }
      elements.cont.find('=a').forEach((a) => a.attr('tabindex', '0'));
      elements.cont.find('=button').forEach((b) => b.attr('tabindex', '0'));
      if (elements.viewMoreLink) {
        elements.viewMoreLink.attr('tabindex', '0');
      }
    });
  }
}
